import React, { useState, useRef, useEffect, useMemo } from "react";
import "./Microsoft.css";
import ServiceContactModal from "../ServiceContactModal/ServiceContactModal"; // adjust path if needed



const microsoftBrandLogo = "https://res.cloudinary.com/dqvvwti2y/image/upload/v1764449152/microsoft-logo-square-transparent-png-2_nhxjiw.png"; // TODO: update path
const copilotLogo = "https://res.cloudinary.com/dqvvwti2y/image/upload/v1764449211/Copilot-Logo_u5tki1.png"; // TODO: update path
const m365Logo = "https://res.cloudinary.com/dqvvwti2y/image/upload/v1764449165/Microsoft-Office-365-Emblem_mn5i1u.png"; // TODO: update path
const azureLogo = "https://res.cloudinary.com/dqvvwti2y/image/upload/v1764449261/Azure-Logo-2020-present_lnlt2e.png"; // TODO: update path
const dynamicsLogo = "https://res.cloudinary.com/dqvvwti2y/image/upload/v1764449326/Dynamics-365-Emblem_ppysuo.png"; // TODO: update path;

const microsoftServices = [
  {
    label: "Microsoft 365 Copilot",
    slug: "copilot",
    featured: true,
    badge: "AI & Productivity",
    logo: copilotLogo,
    logoAlt: "Microsoft 365 Copilot logo",
  },
  {
    label: "Microsoft 365",
    slug: "microsoft-365",
    badge: "Modern Work",
    logo: m365Logo,
    logoAlt: "Microsoft 365 logo",
  },
  {
    label: "Microsoft Azure",
    slug: "azure",
    badge: "Cloud Platform",
    logo: azureLogo,
    logoAlt: "Microsoft Azure logo",
  },
  {
    label: "Dynamics 365",
    slug: "dynamics-365",
    badge: "Business Apps",
    logo: dynamicsLogo,
    logoAlt: "Microsoft Dynamics 365 logo",
  },
];

// Central place for per-service detail content
const microsoftServiceDetails = {
  copilot: {
    title: "Microsoft 365 Copilot Services",
    intro:
      "Bring AI assistance into Outlook, Teams, Word, Excel and Power Platform with secure, governed Copilot rollouts. We help you move beyond hype into real use-cases, measurable productivity gains and safe adoption at scale.",
    points: [
      "Copilot readiness assessment covering licensing, data security, compliance and information architecture.",
      "Mapping of Copilot use-cases for different roles (leadership, sales, finance, HR, operations, support).",
      "Configuration of permissions, data access and governance so Copilot only surfaces what it should.",
      "Pilot programs for key teams including playbooks, prompt libraries and success criteria.",
      "Training sessions to teach your teams how to work with Copilot, not just ‘try’ it once.",
      "Usage analytics, feedback loops and reporting on productivity, quality and adoption over time.",
    ],
  },
  "microsoft-365": {
    title: "Modern Work with Microsoft 365",
    intro:
      "Modernize email, collaboration and device management with a secure, well-structured Microsoft 365 environment. We design your tenant so Teams, SharePoint and OneDrive work together instead of becoming yet another file silo.",
    points: [
      "Tenant setup or clean-up including identities, security baselines, conditional access and MFA.",
      "Design of SharePoint and Teams information architecture so people actually find what they need.",
      "Exchange Online migration, mailbox policies and security controls to reduce phishing and spam risk.",
      "OneDrive rollout strategies and migration from file servers or legacy cloud storage.",
      "Endpoint & mobile device management using Intune aligned to zero-trust and compliance needs.",
      "Change management, communication plans and role-based training to drive adoption of new tools.",
    ],
  },
  azure: {
    title: "Azure Cloud Architecture & Migration",
    intro:
      "Move workloads to Azure with a focus on performance, resilience, security and cost optimization from day one. We design a landing zone that gives your teams freedom to build while keeping control over risk and spend.",
    points: [
      "Cloud strategy and discovery workshops to align Azure plans with your business roadmap.",
      "Azure landing zone architecture (subscriptions, networking, identity, policies, management groups).",
      "Migration planning: which workloads to lift-and-shift, replatform or modernize first.",
      "Implementation of backup, disaster recovery, monitoring and alerting as part of the baseline.",
      "Security hardening including Azure Security Center, Defender, Key Vault and role-based access.",
      "FinOps practices, tagging standards and cost dashboards to keep Azure spend predictable.",
    ],
  },
  "dynamics-365": {
    title: "Dynamics 365 Implementation & Extension",
    intro:
      "Connect sales, service, operations and finance with a tailored Dynamics 365 rollout. We make sure your CRM/ERP reflects your real processes instead of forcing your teams into generic templates.",
    points: [
      "Discovery and requirements mapping across sales, service, marketing, operations and finance teams.",
      "Selection and design of the right Dynamics 365 modules (Sales, Customer Service, Business Central, etc.).",
      "Implementation, configuration and data migration from legacy CRMs, spreadsheets or line-of-business apps.",
      "Integration with Microsoft 365, Azure services and third-party systems (telephony, billing, support tools).",
      "Automation and low-code extensions using Power Apps, Power Automate and Power Virtual Agents.",
      "Ongoing optimization, new feature rollouts and reporting dashboards with Power BI.",
    ],
  },
};

const Microsoft = () => {
  // default selected: first item
  const [activeSlug, setActiveSlug] = useState(microsoftServices[0].slug);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalService, setModalService] = useState("");
  const detailRef = useRef(null);
  const hasInteractedRef = useRef(false);

  const activeService = useMemo(
    () => microsoftServices.find((s) => s.slug === activeSlug),
    [activeSlug]
  );

  const detailContent = microsoftServiceDetails[activeSlug];

  const handleClick = (slug) => {
    setActiveSlug(slug);
    hasInteractedRef.current = true;
  };

  const handleTalkToUs = () => {
    if (activeService?.label) {
      setModalService(activeService.label);
    } else {
      setModalService("Microsoft Cloud Services");
    }
    setIsModalOpen(true);
  };

  // Smooth scroll to details when selection changes (but not on initial mount)
  useEffect(() => {
    if (!hasInteractedRef.current) return;
    if (detailRef.current) {
      detailRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [activeSlug]);

  return (
    <>
      <section className="svc-ms-section svc-ms">
        {/* TOP ROW: intro + menu */}
        <div className="svc-ms-inner">
          <div className="svc-ms-copy">
            <p className="svc-ms-eyebrow ps-eyebrow">CLOUD &amp; PRODUCTIVITY</p>
            <h2 className="svc-ms-title svc-title--hero">
              Smart Microsoft Cloud Solutions
            </h2>
            <p className="svc-ms-sub svc-sub--hero">
              From Microsoft 365 and Copilot to Azure and Dynamics 365, we
              design, deploy and manage Microsoft environments that are secure,
              cost-efficient and easy to run.
            </p>

            <div className="svc-ms-pill-row">
              <span className="svc-ms-pill">Strategy</span>
              <span className="svc-ms-pill">Implementation</span>
              <span className="svc-ms-pill">Managed Services</span>
            </div>

            {/* Microsoft brand row */}
            <div className="svc-ms-brand-row">
              {microsoftBrandLogo && (
                <img
                  src={microsoftBrandLogo}
                  alt="Microsoft logo"
                  className="svc-ms-brand-logo"
                  loading="lazy"
                />
              )}
              <span className="svc-ms-brand-text">
                Built on the official Microsoft cloud stack
              </span>
            </div>
          </div>

          <div className="svc-ms-card" aria-label="Microsoft services">
            <div className="svc-ms-card-header">
              <p>Choose a Microsoft workload to explore</p>
            </div>
            <ul className="svc-ms-list">
              {microsoftServices.map((item) => {
                const isActive = item.slug === activeSlug;
                return (
                  <li
                    key={item.slug}
                    className={[
                      "svc-ms-item",
                      item.featured ? "svc-ms-item--featured" : "",
                      isActive ? "svc-ms-item--active" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => handleClick(item.slug)}
                  >
                    <div className="svc-ms-item-left">
                      {item.logo && (
                        <img
                          src={item.logo}
                          alt={item.logoAlt || item.label}
                          className="svc-ms-item-logo"
                          loading="lazy"
                        />
                      )}
                      <div className="svc-ms-label-wrap">
                        <span className="svc-ms-label">{item.label}</span>
                        {item.badge && (
                          <span className="svc-ms-badge">{item.badge}</span>
                        )}
                      </div>
                    </div>
                    <span className="svc-ms-arrow" aria-hidden="true">
                      ›
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* BOTTOM CONTENT: service-specific details + process / stats */}
        <div className="svc-ms-bottom" ref={detailRef}>
          <div className="svc-ms-bottom-inner">
            <div className="svc-ms-bottom-copy">
              <div className="svc-ms-logo-title">
  {activeService?.logo && (
    <img
      src={activeService.logo}
      alt={activeService.logoAlt || activeService.label}
      className="svc-ms-detail-logo"
    />
  )}
  <h3>
    {detailContent?.title ||
      `How we help you get more from Microsoft Cloud`}
  </h3>
</div>

              <p>
                {detailContent?.intro ||
                  `Whether you're just starting with Microsoft 365 or already
                running complex Azure workloads, we provide a structured approach
                to planning, rollout and ongoing optimization.`}
              </p>

              {detailContent?.points && (
                <ul className="svc-ms-detail-list">
                  {detailContent.points.map((point, idx) => (
                    <li key={idx}>{point}</li>
                  ))}
                </ul>
              )}

              {activeService && (
                <p className="svc-ms-detail-tag">
                  Currently viewing: <span>{activeService.label}</span>
                </p>
              )}

              <div className="svc-ms-cta-row">
                <button
                  type="button"
                  className="svc-ms-cta-primary"
                  onClick={handleTalkToUs}
                >
                  Talk to us about {activeService?.label || "Microsoft Cloud"}
                </button>
                <p className="svc-ms-cta-note">
                  No lock-in. We’ll respond with practical next steps, not a
                  generic pitch.
                </p>
              </div>
            </div>

            {/* Reuse your generic 3-step process */}
            <div className="svc-ms-bottom-grid">
              <div className="svc-ms-bottom-item">
                <h4>Plan & Architect</h4>
                <p>
                  We assess your current stack, security posture and licensing,
                  then design a Microsoft roadmap that aligns with your business
                  goals and budget.
                </p>
              </div>

              <div className="svc-ms-bottom-item">
                <h4>Implement & Migrate</h4>
                <p>
                  Seamless rollout of Microsoft 365, Azure or Dynamics 365 with
                  data migration, integrations and governance baked in from day
                  one.
                </p>
              </div>

              <div className="svc-ms-bottom-item">
                <h4>Adopt & Optimize</h4>
                <p>
                  Training, automation and ongoing monitoring to unlock Copilot,
                  Teams, Power Platform and reduce cloud spend over time.
                </p>
              </div>
            </div>

            <div className="svc-ms-stats">
              <div className="svc-stat">
                <span className="svc-stat-number">30%+</span>
                <span className="svc-stat-label">
                  Average productivity gain
                </span>
              </div>
              <div className="svc-stat">
                <span className="svc-stat-number">99.9%</span>
                <span className="svc-stat-label">Cloud uptime targets</span>
              </div>
              <div className="svc-stat">
                <span className="svc-stat-number">24/7</span>
                <span className="svc-stat-label">Managed support options</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Modal */}
      <ServiceContactModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        defaultService={modalService}
      />
    </>
  );
};

export default Microsoft;
